import React from 'react';
import { useHistory } from 'react-router';
import clsx from 'clsx';
import { ArrowDownIcon, ArrowUpIcon } from 'assets/icons';
import { isUrlInChildren } from 'new-design/components/CmtHelpers/JssHelper';
import amberTheme from 'styles/themes/amberTheme';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { TreeItem, TreeView } from '@material-ui/lab';

const useTreeItemStyles = makeStyles((theme) =>
  createStyles({
    root: {
      color: theme.palette.text.secondary,
      '&:hover > $content': {
        backgroundColor: 'transparent', // theme.palette.action.hover,
      },
      '&:focus > $content, &$selected > $content': {
        backgroundColor: 'transparent', // `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
        color: 'var(--tree-view-color)',
      },
      '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected.sub-item .MuiTreeItem-label svg path': {
        fill: theme.palette.primary.main,
      },
      '&.Mui-selected.sub-item .MuiTreeItem-label svg rect': {
        fill: theme.palette.primary.main,
      },
      '& .active': {
        color: theme.palette.primary.main,
        '& svg path': {
          fill: theme.palette.primary.main,
        },
        '& svg rect': {
          fill: theme.palette.primary.main,
        },

        '& svg': {
          '& path.stroke': {
            stroke: theme.palette.primary.main,
          },
          '& path.fill': {
            fill: theme.palette.primary.main,
          },
        },
      },
    },
    content: {
      display: 'flex',
      flexDirection: 'row-reverse',
      color: theme.palette.text.secondary,
      borderTopRightRadius: theme.spacing(2),
      borderBottomRightRadius: theme.spacing(2),
      fontWeight: theme.typography.fontWeightMedium,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      '$expanded > &': {
        fontWeight: theme.typography.fontWeightRegular,
      },
    },
    group: {
      marginLeft: 0,
      '& $content': {
        paddingLeft: theme.spacing(2),
      },
    },
    expanded: {},
    selected: {},
    label: {
      fontWeight: 'inherit',
      color: 'inherit',
    },
    labelRoot: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.5, 0),
    },
    labelIcon: {
      width: 24,
      height: 24,
      marginRight: theme.spacing(2),
      '& svg': {
        width: 24,
        height: 24,
        '& path': {
          fill: theme.palette.secondary.main,
        },
        '& rect': {
          fill: theme.palette.secondary.main,
        },
        '& path.stroke': {
          stroke: theme.palette.secondary.main,
        },
        '& path.fill': {
          fill: theme.palette.secondary.main,
        },
      },
    },
    labelText: {
      flexGrow: 1,
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
    },
    iconContainer: {
      width: 24,
      height: 24,
      marginRight: 0,
      '& svg path': {
        stroke: theme.palette.secondary.main,
      },
    },
  })
);

function MobileMenuItem({ labelText, labelIcon, color, bgColor, active, ...other }) {
  const classes = useTreeItemStyles();

  return (
    <TreeItem
      label={
        <div
          className={clsx(classes.labelRoot, {
            active,
          })}
        >
          <div className={classes.labelIcon}>{labelIcon}</div>
          <Typography variant="body2" className={classes.labelText}>
            {labelText}
          </Typography>
        </div>
      }
      style={{
        '--tree-view-color': `${color}`,
        '--tree-view-bg-color': bgColor,
      }}
      classes={{
        root: classes.root,
        content: classes.content,
        expanded: classes.expanded,
        selected: classes.selected,
        group: classes.group,
        label: classes.label,
        iconContainer: classes.iconContainer,
      }}
      {...other}
    />
  );
}

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        overflow: 'auto',
        height: 'calc(100vh - 280px)',
        marginTop: theme.spacing(3),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
      },
    }),
  { name: 'MobileMenu' }
);

export const MobileMenu = ({ menuItems, handleCloseMobile }) => {
  const classes = useStyles();
  const history = useHistory();

  const onItemClick = (link) => {
    history.push(link);
    handleCloseMobile();
  };

  const renderMenu = (menuItems) => {
    const menu = [];

    menuItems.forEach((item, index) => {
      if (item.children.length) {
        menu.push(
          <MobileMenuItem
            key={`mobile-parent-${item.name.props.id}`}
            nodeId={index.toString()}
            labelText={item.name}
            labelIcon={item.icon}
          >
            {item.children.map((child, childIndex) => (
              <MobileMenuItem
                className="sub-item"
                key={`mobile-child-${child.name}`}
                nodeId={`${index}-${childIndex}`}
                labelText={child.name}
                labelIcon={child.icon}
                labelInfo="90"
                color={amberTheme.palette.primary.main}
                bgColor={amberTheme.palette.secondary.light}
                active={isUrlInChildren(child, history.location.pathname)}
                onClick={() => onItemClick(child.link)}
              />
            ))}
          </MobileMenuItem>
        );
      } else {
        menu.push(
          <MobileMenuItem
            className="sub-item"
            key={`mobile-parent-${item.name.props.id}`}
            nodeId={`${index}`}
            labelText={item.name}
            labelIcon={item.icon}
            color={amberTheme.palette.primary.main}
            bgColor={amberTheme.palette.secondary.light}
            active={isUrlInChildren(item, history.location.pathname)}
            onClick={() => onItemClick(item.link)}
          />
        );
      }
    });

    return menu;
  };

  return (
    <TreeView
      className={classes.root}
      defaultCollapseIcon={<ArrowUpIcon />}
      defaultExpandIcon={<ArrowDownIcon />}
    >
      {renderMenu(menuItems)}
    </TreeView>
  );
};
